'use client';

import { Padding, Typography } from '@musinsa/mds';
import { convertToFullUrl, platformIdToSnsType } from '@musinsa/services-curator';
import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { PageViewTracker } from '../../../common/components';
import { useSnsDuplicateCheck } from '../../../common/hooks/useSnsDuplicateCheck';
import { useFunnelContext } from '../../context/FunnelContext';
import { PlatformForm } from './components/PlatformForm';
import { SecondPlatformSection } from './components/SecondPlatformSection';
import { PLATFORM_INDEX, type PlatformIndex } from './constants';
import type { Platform } from './constants/platforms';
import { useSNSForm } from './hooks/useSNSForm';

interface Props {
  className?: string;
}

const buildFullUrl = (platform: Platform | undefined, inputValue: string): string => {
  const trimmedValue = inputValue.trim();
  if (trimmedValue.length === 0) {
    return '';
  }

  if (!platform) {
    return trimmedValue;
  }

  const snsType = platformIdToSnsType(platform.id);
  return convertToFullUrl(snsType, trimmedValue);
};

export const SNSFunnel = ({ className }: Props) => {
  const { formValues, updateFormValues, updateDuplicationResult, duplicationResultsByIndex } =
    useFunnelContext();

  const {
    showSecondPlatform,
    firstDropdownOpen,
    setFirstDropdownOpen,
    secondDropdownOpen,
    setSecondDropdownOpen,
    getAccountData,
    onPlatformSelect,
    onUrlChange,
    onUrlClear,
    addSecondPlatform,
    removeSecondPlatform,
    etcUrlErrors,
  } = useSNSForm({ formValues, onUpdate: updateFormValues });

  const firstAccount = getAccountData(PLATFORM_INDEX.FIRST_PLATFORM);
  const secondAccount = getAccountData(PLATFORM_INDEX.SECOND_PLATFORM);

  const handleUrlChange = (index: PlatformIndex, url: string) => {
    onUrlChange(index, url);
  };

  const handlePlatformSelect = (index: PlatformIndex) => (platform: Platform) => {
    onPlatformSelect(index, platform);
  };

  const firstFullUrl = useMemo(
    () => buildFullUrl(firstAccount.selectedPlatform, firstAccount.platformUrl),
    [firstAccount.selectedPlatform, firstAccount.platformUrl],
  );

  const secondFullUrl = useMemo(
    () => buildFullUrl(secondAccount.selectedPlatform, secondAccount.platformUrl),
    [secondAccount.selectedPlatform, secondAccount.platformUrl],
  );

  const firstHasEtcError = Boolean(etcUrlErrors[PLATFORM_INDEX.FIRST_PLATFORM]);
  const secondHasEtcError = Boolean(etcUrlErrors[PLATFORM_INDEX.SECOND_PLATFORM]);

  useSnsDuplicateCheck({
    url: firstFullUrl,
    enabled: firstFullUrl.length > 0 && !firstHasEtcError,
    debounceDelay: 500,
    onStatusChange: (status) => updateDuplicationResult(PLATFORM_INDEX.FIRST_PLATFORM, status),
  });

  useSnsDuplicateCheck({
    url: secondFullUrl,
    enabled: showSecondPlatform && secondFullUrl.length > 0 && !secondHasEtcError,
    debounceDelay: 500,
    onStatusChange: (status) => updateDuplicationResult(PLATFORM_INDEX.SECOND_PLATFORM, status),
  });

  const firstDuplicateState = duplicationResultsByIndex[PLATFORM_INDEX.FIRST_PLATFORM];
  const secondDuplicateState = duplicationResultsByIndex[PLATFORM_INDEX.SECOND_PLATFORM];

  const firstIsDuplicated = firstDuplicateState?.isDuplicated === true;
  const secondIsDuplicated = secondDuplicateState?.isDuplicated === true;

  return (
    <>
      <PageViewTracker
        pageName="curator_signup"
        musinsaPageName=""
        snowplowProperties={{
          stage_status: 'step2_success',
        }}
      />
      <div className={twMerge('w-full px-4 grow pb-[72px]!', className)}>
        <Padding top="6" bottom="4" className="leading-6">
          <Typography variant="title18pxMed">
            예비 큐레이터님의
            <br />
            SNS에 대한 정보를 공유해 주세요.
          </Typography>
        </Padding>

        <PlatformForm
          platform={firstAccount.selectedPlatform}
          url={firstAccount.platformUrl}
          onPlatformSelect={handlePlatformSelect(PLATFORM_INDEX.FIRST_PLATFORM)}
          onUrlChange={(url) => handleUrlChange(PLATFORM_INDEX.FIRST_PLATFORM, url)}
          onUrlClear={() => onUrlClear(PLATFORM_INDEX.FIRST_PLATFORM)}
          title="현재 팔로워 수가 가장 많은 플랫폼은 무엇인가요?"
          showRemoveButton={false}
          isDropdownOpen={firstDropdownOpen}
          onDropdownOpenChange={setFirstDropdownOpen}
          isDuplicated={firstIsDuplicated}
          urlValidationError={etcUrlErrors[PLATFORM_INDEX.FIRST_PLATFORM]}
        />

        <SecondPlatformSection
          showSecondPlatform={showSecondPlatform}
          platform={secondAccount.selectedPlatform}
          url={secondAccount.platformUrl}
          onAddSecondPlatform={addSecondPlatform}
          onRemoveSecondPlatform={removeSecondPlatform}
          onPlatformSelect={handlePlatformSelect(PLATFORM_INDEX.SECOND_PLATFORM)}
          onUrlChange={(url) => handleUrlChange(PLATFORM_INDEX.SECOND_PLATFORM, url)}
          onUrlClear={() => onUrlClear(PLATFORM_INDEX.SECOND_PLATFORM)}
          isDropdownOpen={secondDropdownOpen}
          onDropdownOpenChange={setSecondDropdownOpen}
          isDuplicated={secondIsDuplicated}
          urlValidationError={etcUrlErrors[PLATFORM_INDEX.SECOND_PLATFORM]}
        />
      </div>
    </>
  );
};
